import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Activity, Wallet, Users, ArrowDownLeft, ArrowUpRight, Power, PowerOff, RefreshCw, Loader2, ExternalLink, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import setIfChanged from '../../lib/setIfChanged';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtTime = (iso) => {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch { return iso; }
};

const METHOD_COLOR = {
    addAccount: '#39FF14',
    setScore: '#FFD700',
    getAccount: '#A0AAB2',
    launchGame: '#00FFFF',
    reloadPurchase: '#C77DFF',
    initialShift: '#FF007F',
    closeShift: '#FF3B30',
    editKioskSetting: '#FFA500',
};

const CasinoSyncAdminTab = () => {
    const [bridge, setBridge] = useState(null);
    const [agent, setAgent] = useState(null);
    const [players, setPlayers] = useState([]);
    const [audit, setAudit] = useState([]);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [filter, setFilter] = useState('');
    const [busy, setBusy] = useState(false);

    const loadAll = useCallback(async () => {
        try {
            const [b, a, p, au] = await Promise.allSettled([
                axios.get(`${API}/admin/majik/bridge-health`, { withCredentials: true }),
                axios.get(`${API}/admin/majik/agent-balance`, { withCredentials: true }),
                axios.get(`${API}/admin/majik/players`, { withCredentials: true }),
                axios.get(`${API}/admin/majik/recent-audit?limit=80`, { withCredentials: true }),
            ]);
            setBridge((prev) => setIfChanged(prev, b.status === 'fulfilled' ? b.value.data : { ok: false, error: b.reason?.response?.data?.detail }));
            setAgent((prev) => setIfChanged(prev, a.status === 'fulfilled' ? a.value.data : null));
            setPlayers((prev) => setIfChanged(prev, p.status === 'fulfilled' ? p.value.data?.players || [] : []));
            setAudit((prev) => setIfChanged(prev, au.status === 'fulfilled' ? au.value.data?.audit || [] : []));
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to load casino sync');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAll();
        const tick = () => { if (!document.hidden) loadAll(); };
        const id = setInterval(tick, 30_000);
        return () => clearInterval(id);
    }, [loadAll]);

    const closeDrawer = async () => {
        if (!window.confirm('Close the cashier shift? You\'ll need to reopen it before any topups/redeems work.')) return;
        setBusy(true);
        try {
            await axios.post(`${API}/admin/majik/close-drawer`, {}, { withCredentials: true });
            toast.success('Shift closed');
            await loadAll();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to close drawer');
        } finally {
            setBusy(false);
        }
    };

    const filteredAudit = filter
        ? audit.filter(a => JSON.stringify(a).toLowerCase().includes(filter.toLowerCase()))
        : audit;

    const playersWithRedeemable = players.filter(p => p.redeem_total === 0 && p.topup_total > 0);

    return (
        <div className="space-y-6" data-testid="casino-sync-admin">
            {/* Health strip */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <HealthCard
                    label="BRIDGE"
                    ok={!!bridge?.ok}
                    detail={bridge?.bridge_url || bridge?.error || '—'}
                    icon={<Activity className="w-4 h-4" />}
                />
                <HealthCard
                    label="AGENT SCORE"
                    ok={!!agent}
                    detail={agent ? fmt$(agent.score) : 'unavailable'}
                    icon={<Wallet className="w-4 h-4" />}
                    color="#FFD700"
                />
                <HealthCard
                    label="PROVISIONED PLAYERS"
                    ok={true}
                    detail={`${players.length} active`}
                    icon={<Users className="w-4 h-4" />}
                    color="#39FF14"
                />
                <HealthCard
                    label="SHIFT"
                    ok={true}
                    detail={agent ? 'Drawer open' : '—'}
                    icon={<Power className="w-4 h-4" />}
                    color="#00FFFF"
                />
            </div>

            {/* Quick actions */}
            <div className="flex flex-wrap gap-2">
                <Button onClick={() => setDrawerOpen(true)} className="bg-[#39FF14] hover:bg-[#2DD30F] text-black font-bold" data-testid="casino-admin-open-drawer-btn">
                    <Power className="w-4 h-4" />
                    Open / Reset Shift
                </Button>
                <Button onClick={closeDrawer} disabled={busy} variant="outline" className="border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10" data-testid="casino-admin-close-drawer-btn">
                    <PowerOff className="w-4 h-4" />
                    Close Shift
                </Button>
                <Button onClick={loadAll} variant="ghost" className="text-[#A0AAB2] hover:text-white" data-testid="casino-admin-refresh-btn">
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                </Button>
            </div>

            {/* Reconciliation alert */}
            {playersWithRedeemable.length > 0 && (
                <div className="rounded-xl border border-[#FFD700]/30 bg-[#FFD700]/5 p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-[#FFD700] shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm font-bold text-[#FFD700]">{playersWithRedeemable.length} player{playersWithRedeemable.length !== 1 ? 's' : ''} with topups but no redemptions</p>
                        <p className="text-xs text-[#A0AAB2] mt-1">These players may have winnings sitting in their Majik account that haven't been pulled back to BETohalics yet.</p>
                    </div>
                </div>
            )}

            {/* Player table */}
            <div className="rounded-xl border border-white/10 bg-[#0F0F0F] overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#FFD700]" />
                    <h3 className="text-sm font-black tracking-wider text-white">SLOTS PLAYERS</h3>
                    <span className="ml-auto text-xs text-[#A0AAB2]">{players.length}</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-white/[0.02] text-[#A0AAB2]">
                            <tr>
                                <th className="px-3 py-2 text-left font-semibold">Player</th>
                                <th className="px-3 py-2 text-left font-semibold">Majik Account</th>
                                <th className="px-3 py-2 text-right font-semibold">Topups</th>
                                <th className="px-3 py-2 text-right font-semibold">Redeems</th>
                                <th className="px-3 py-2 text-right font-semibold">Net Flow</th>
                                <th className="px-3 py-2 text-right font-semibold">Last Activity</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="6" className="px-3 py-6 text-center text-[#A0AAB2]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
                            ) : players.length === 0 ? (
                                <tr><td colSpan="6" className="px-3 py-6 text-center text-[#A0AAB2]">No players have launched the casino yet.</td></tr>
                            ) : (
                                players.map(p => (
                                    <tr key={p.user_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`casino-admin-player-${p.user_id}`}>
                                        <td className="px-3 py-2.5">
                                            <div className="text-white font-semibold">{p.name}</div>
                                            <div className="text-[10px] text-[#A0AAB2]">{p.email}</div>
                                        </td>
                                        <td className="px-3 py-2.5 font-mono text-[#FFD700]">{p.majik_account}</td>
                                        <td className="px-3 py-2.5 text-right">
                                            <div className="text-[#39FF14] tabular-nums">{fmt$(p.topup_total)}</div>
                                            <div className="text-[10px] text-[#A0AAB2]">{p.topup_count}×</div>
                                        </td>
                                        <td className="px-3 py-2.5 text-right">
                                            <div className="text-[#FFD700] tabular-nums">{fmt$(p.redeem_total)}</div>
                                            <div className="text-[10px] text-[#A0AAB2]">{p.redeem_count}×</div>
                                        </td>
                                        <td className="px-3 py-2.5 text-right tabular-nums font-bold" style={{ color: p.net_flow >= 0 ? '#39FF14' : '#FF3B30' }}>
                                            {p.net_flow >= 0 ? '+' : ''}{fmt$(p.net_flow)}
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-[#A0AAB2]">{fmtTime(p.last_activity)}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Audit log */}
            <div className="rounded-xl border border-white/10 bg-[#0F0F0F] overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 flex-wrap">
                    <ExternalLink className="w-4 h-4 text-[#00FFFF]" />
                    <h3 className="text-sm font-black tracking-wider text-white">API AUDIT LOG</h3>
                    <Input
                        placeholder="filter (method, player, account, error...)"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="ml-auto bg-[#121212] border-white/10 text-white text-xs h-7 w-64"
                        data-testid="casino-admin-audit-filter"
                    />
                    <span className="text-xs text-[#A0AAB2]">{filteredAudit.length}</span>
                </div>
                <div className="max-h-[420px] overflow-y-auto divide-y divide-white/5">
                    {filteredAudit.length === 0 ? (
                        <div className="px-4 py-6 text-center text-[#A0AAB2] text-xs">No audit entries.</div>
                    ) : (
                        filteredAudit.map((a, idx) => {
                            const c = METHOD_COLOR[a.method] || '#A0AAB2';
                            const status = a.response?.status;
                            const isErr = a.error || (status && status !== 'success');
                            return (
                                <details key={idx} className="px-4 py-2 group" data-testid={`casino-admin-audit-row-${idx}`}>
                                    <summary className="cursor-pointer text-xs flex items-center gap-2 select-none">
                                        <span className="font-mono px-2 py-0.5 rounded text-[10px] font-black" style={{ background: `${c}22`, color: c, border: `1px solid ${c}55` }}>
                                            {a.method}
                                        </span>
                                        {isErr ? <AlertCircle className="w-3 h-3 text-[#FF3B30]" /> : <CheckCircle2 className="w-3 h-3 text-[#39FF14]" />}
                                        <span className="text-[#A0AAB2] flex-1 truncate">
                                            {a.request?.account ? `acct ${a.request.account}` : ''}
                                            {a.request?.setScore ? ` · setScore ${a.request.setScore}` : ''}
                                            {a.response?.message ? ` · ${a.response.message}` : ''}
                                            {a.error ? ` · ${a.error}` : ''}
                                        </span>
                                        <span className="text-[10px] text-[#666] shrink-0">{fmtTime(a.created_at)}</span>
                                    </summary>
                                    <pre className="mt-2 text-[10px] bg-black/60 p-2 rounded overflow-x-auto text-[#A0AAB2]">{JSON.stringify({ request: a.request, response: a.response, error: a.error, http_status: a.http_status }, null, 2)}</pre>
                                </details>
                            );
                        })
                    )}
                </div>
            </div>

            <OpenDrawerDialog open={drawerOpen} onClose={() => setDrawerOpen(false)} onSuccess={loadAll} />
        </div>
    );
};

const HealthCard = ({ label, ok, detail, icon, color = '#39FF14' }) => (
    <div className="rounded-xl border border-white/10 bg-[#0F0F0F] p-3" data-testid={`casino-admin-health-${label.toLowerCase().replace(/\s+/g, '-')}`}>
        <div className="flex items-center gap-1.5 text-[10px] font-black tracking-wider text-[#A0AAB2]">
            {icon}{label}
            <span className={`ml-auto w-1.5 h-1.5 rounded-full ${ok ? 'bg-[#39FF14]' : 'bg-[#FF3B30]'} animate-pulse`} />
        </div>
        <p className="text-lg font-black mt-1 tabular-nums truncate" style={{ color: ok ? color : '#FF3B30' }} title={detail}>
            {detail}
        </p>
    </div>
);

const OpenDrawerDialog = ({ open, onClose, onSuccess }) => {
    const [amount, setAmount] = useState('10');
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        const amt = parseInt(amount, 10);
        if (!Number.isInteger(amt) || amt < 0) {
            toast.error('Amount must be a positive integer');
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/admin/majik/open-drawer`, { initial_amount: amt, store_name: 'BETohalics' }, { withCredentials: true });
            toast.success('Drawer open');
            onClose();
            onSuccess?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to open drawer');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="bg-[#0B0B0B] border-[#39FF14]/30">
                <DialogHeader><DialogTitle className="text-white">Open Cashier Shift</DialogTitle></DialogHeader>
                <p className="text-xs text-[#A0AAB2]">Starts a fresh cashier shift on MajikBonus. Initial amount must be a whole number (Majik requirement).</p>
                <Input
                    type="number" min="0" step="1" value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="bg-[#121212] text-white text-xl font-bold h-12"
                    data-testid="casino-admin-open-drawer-amount"
                />
                <div className="flex gap-2 mt-2">
                    <Button variant="ghost" onClick={onClose} className="flex-1" disabled={busy}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="flex-1 bg-[#39FF14] hover:bg-[#2DD30F] text-black font-black" data-testid="casino-admin-open-drawer-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Open Shift'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default CasinoSyncAdminTab;
